// features/admin/serviceApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface Service {
  id: number;
  title: string;
  description: string | null;
  sort_order: number;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

export interface ServiceApiResponse<T = any> {
  error: boolean;
  message?: string;
  total?: number;
  data?: T;
}

// ─── Request Types ────────────────────────────────────────────────────────────

export interface AddServiceRequest {
  title: string;
  description?: string;
  sort_order?: number;
  is_active?: number;
}

export interface UpdateServiceRequest {
  id: number;
  title?: string;
  description?: string;
  sort_order?: number;
  is_active?: number;
}

export interface DeleteServiceRequest {
  id: number;
}

// ─── API Slice ────────────────────────────────────────────────────────────────

export const serviceApi = createApi({
  reducerPath: 'serviceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Services'],
  endpoints: (build) => ({

    // ── List (GET — no auth) ──────────────────────────────────────────────────
    listServices: build.query<ServiceApiResponse<Service[]>, void>({
      query: () => ({
        url:    '/globalsettings/listServices',
        method: 'GET',
      }),
      providesTags: ['Services'],
      transformResponse: (response: any): ServiceApiResponse<Service[]> => ({
        error:   response?.error ?? false,
        message: response?.message || '',
        total:   response?.total  ?? 0,
        data:    response?.data   || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to load services', data: [],
      }),
    }),

    // ── Add ───────────────────────────────────────────────────────────────────
    addService: build.mutation<ServiceApiResponse, AddServiceRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('title', body.title);
        if (body.description !== undefined) formData.append('description', body.description);
        if (body.sort_order  !== undefined) formData.append('sort_order',  String(body.sort_order));
        if (body.is_active   !== undefined) formData.append('is_active',   String(body.is_active));
        return {
          url:    '/globalsettings/addService',
          method: 'POST',
          body:   formData.toString(),
        };
      },
      invalidatesTags: ['Services'],
      transformResponse: (response: any): ServiceApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Service added successfully',
        data:    response?.id,
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to add service',
      }),
    }),

    // ── Update ────────────────────────────────────────────────────────────────
    updateService: build.mutation<ServiceApiResponse, UpdateServiceRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('id', String(body.id));
        if (body.title       !== undefined) formData.append('title',       body.title);
        if (body.description !== undefined) formData.append('description', body.description);
        if (body.sort_order  !== undefined) formData.append('sort_order',  String(body.sort_order));
        if (body.is_active   !== undefined) formData.append('is_active',   String(body.is_active));
        return {
          url:    '/globalsettings/editService',
          method: 'POST',
          body:   formData.toString(),
        };
      },
      invalidatesTags: ['Services'],
      transformResponse: (response: any): ServiceApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Service updated successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to update service',
      }),
    }),

    // ── Delete ────────────────────────────────────────────────────────────────
    deleteService: build.mutation<ServiceApiResponse, DeleteServiceRequest>({
      query: (body) => ({
        url:    '/globalsettings/deleteService',
        method: 'POST',
        body:   new URLSearchParams({ id: String(body.id) }).toString(),
      }),
      invalidatesTags: ['Services'],
      transformResponse: (response: any): ServiceApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Service deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to delete service',
      }),
    }),

  }),
});

export const {
  useListServicesQuery,
  useAddServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
} = serviceApi;